import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import React, { FC, useEffect, useState } from 'react'
import InfoBlock from '@mortgage-pos/ui/components/InfoBlock'
import LoadingWithContent from './LoadingWithContent'

interface Props {
  title?: string
  subTitle?: string
  apiCall: string
  minimumTimeout?: number
  errorMessage?: string
  continueOnError?: boolean
}

const LoaderWithAPICalls: FC<Props> = ({
  title = '',
  subTitle = '',
  apiCall,
  minimumTimeout = 2,
  errorMessage = '',
  continueOnError = false,
}) => {
  const loaderTimeout = Math.min(minimumTimeout * 1000, 20000) // 20 second max timer

  const [isAPICallComplete, setIsAPICallComplete] = useState<boolean>(false)
  const [isMinTimeoutComplete, setIsMinTimeoutComplete] =
    useState<boolean>(false)
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false)

  const { getAVMData } = useStoreActions((store) => store.propertyInfo)
  const { next } = useStoreActions((store) => store.questionnaire)
  const { softPullCredit } = useStoreActions((store) => store.application)

  // Hook to start the minimum timer to prevent screen flashing
  useEffect(() => {
    const startMinimumTimeout = async () => {
      const res = await new Promise(function (res, rej) {
        setTimeout(() => res('finished'), loaderTimeout)
      })

      if (res) {
        setIsMinTimeoutComplete(true)
      }
    }

    startMinimumTimeout()
  }, [])

  // Hook to handle API calls
  useEffect(() => {
    const makeAPIRequests = async () => {
      let res

      try {
        switch (apiCall?.toLowerCase()) {
          case 'avmpropertyinfo':
            res = await getAVMData()
            break
          case 'softpullcredit':
            res = await softPullCredit()
            break
          default:
            if (!continueOnError) {
              setShowErrorMessage(true)
            }
            break
        }
      } catch (e) {
        setShowErrorMessage(true)
      }

      const isSuccessful = await didApiSucceed(apiCall, res)

      if (!isSuccessful && !continueOnError) {
        setShowErrorMessage(true)
      }
      setIsAPICallComplete(true)
    }

    // if there's no API call from storyblok, treat this as a
    // timed loader
    !apiCall ? setIsAPICallComplete(true) : makeAPIRequests()
  }, [])

  useEffect(() => {
    const onFinish = async () => {
      if (!continueOnError && showErrorMessage) {
        return
      }
      if (isAPICallComplete && isMinTimeoutComplete) {
        await next()
      }
    }

    onFinish()
  }, [
    isAPICallComplete,
    isMinTimeoutComplete,
    continueOnError,
    showErrorMessage,
  ])

  return (
    <>
      {showErrorMessage ? (
        <InfoBlock type="danger">
          {errorMessage || 'Something went wrong, please try again'}
        </InfoBlock>
      ) : null}
      {!showErrorMessage ? (
        <LoadingWithContent body={subTitle} title={title} />
      ) : null}
    </>
  )
}

export default LoaderWithAPICalls

const didApiSucceed = async (apiCall, response) => {
  switch (apiCall?.toLowerCase()) {
    case 'avmpropertyinfo':
      // this value should be a number when successful
      return !!response
    case 'softpullcredit':
      // this endpoint returns a boolean
      return response
    case 'corelogicsoftpull':
      // Always return true b/c we want to proceed
      // regardless of whether this worked or not
      return true
    default:
      break
  }
}
